/**
 * Cookie banner: Accept / Decline (equal weight) and Manage preferences.
 *
 * Essential cookies (signing in, keeping the site working) are always on. "Measurement" covers the
 * first-party cookies that show which links, posts and newsletters bring people to Voucha
 * (voucha-ads' vch_vid / vch_attr): the server only reads or sets them when the choice below is "all",
 * and removes them after a Decline.
 *
 * "Advertising" covers the Meta Pixel and Google tag that show which of our ads on Meta and Google bring
 * people to Voucha (voucha-assessments' tracking.js). They load only when vch_ads is "yes".
 *
 * Choices are kept for 6 months: vch_consent (all | essential) and vch_ads (yes | no). Anyone who chose
 * before the Advertising choice existed has no vch_ads, so the banner opens once more to ask.
 * "Cookie settings" in the sidebar footer, or any link to #cookie-settings, opens the choices again.
 * Every choice fires a "voucha:cookie-choice" event on window with { measurement, advertising }.
 */
import app from 'flarum/forum/app';
const COOKIE = 'vch_consent';
const ADS_COOKIE = 'vch_ads';
const MAX_AGE = 60 * 60 * 24 * 182;
const readCookie = name => {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};
const read = () => readCookie(COOKIE);
const readAds = () => readCookie(ADS_COOKIE);
const save = (name, value) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = name + '=' + value + '; Max-Age=' + MAX_AGE + '; Path=/; SameSite=Lax' + secure;
};

/** Meta and Google cookies set on this site, removed when Advertising is switched off */
function clearAdCookies() {
  const names = document.cookie.split(';').map(c => c.split('=')[0].trim()).filter(n => n === '_fbp' || n === '_fbc' || n.indexOf('_gcl') === 0);
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = ['', host, '.' + host];
  if (parts.length > 2) domains.push('.' + parts.slice(-2).join('.'));
  names.forEach(n => {
    domains.forEach(d => {
      document.cookie = n + '=; Max-Age=0; Path=/' + (d ? '; Domain=' + d : '');
    });
  });
}
let box = null;
function close() {
  if (box) {
    box.remove();
    box = null;
  }
}
function choose(measurement, advertising) {
  const value = measurement ? 'all' : 'essential';
  const changed = read() !== value;
  save(COOKIE, value);
  save(ADS_COOKIE, advertising ? 'yes' : 'no');
  close();
  if (!advertising) clearAdCookies();
  try {
    window.dispatchEvent(new CustomEvent('voucha:cookie-choice', {
      detail: {
        measurement,
        advertising
      }
    }));
  } catch (e) {}
  // A Decline (or switching measurement off) clears old cookies on the next request; make one now
  if (changed && value !== 'all') {
    try {
      fetch(app.forum.attribute('apiUrl') + '/voucha-ads/page', {
        credentials: 'same-origin'
      }).catch(() => {});
    } catch (e) {}
  }
}
function el(tag, attrs, children) {
  const n = document.createElement(tag);
  Object.entries(attrs || {}).forEach(_ref => {
    let [k, v] = _ref;
    if (k === 'text') n.textContent = v;else if (k.startsWith('on')) n.addEventListener(k.slice(2), v);else n.setAttribute(k, v);
  });
  (children || []).forEach(c => c && n.appendChild(c));
  return n;
}

/** Show the banner; with `prefs` the preferences are open (from "Cookie settings") */
export function openCookieBanner(prefs) {
  if (prefs === void 0) {
    prefs = false;
  }
  close();
  const current = read();
  const currentAds = readAds();
  const measure = el('input', {
    type: 'checkbox',
    id: 'VouchaCookies-measure'
  });
  measure.checked = current === 'all';
  const ads = el('input', {
    type: 'checkbox',
    id: 'VouchaCookies-ads'
  });
  ads.checked = currentAds === 'yes';
  const prefsPanel = el('div', {
    class: 'VouchaCookies-prefs'
  }, [el('div', {
    class: 'VouchaCookies-row'
  }, [el('div', {}, [el('strong', {
    text: 'Essential'
  }), el('span', {
    text: 'Keep you signed in and the site working.'
  })]), el('span', {
    class: 'VouchaCookies-always',
    text: 'Always on'
  })]), el('div', {
    class: 'VouchaCookies-row'
  }, [el('label', {
    for: 'VouchaCookies-measure'
  }, [el('strong', {
    text: 'Measurement'
  }), el('span', {
    text: 'Show us which links, posts and newsletters bring people to Voucha. First-party only; never sold.'
  })]), el('span', {
    class: 'VouchaCookies-switch'
  }, [measure, el('span', {
    'aria-hidden': 'true'
  })])]), el('div', {
    class: 'VouchaCookies-row'
  }, [el('label', {
    for: 'VouchaCookies-ads'
  }, [el('strong', {
    text: 'Advertising'
  }), el('span', {
    text: 'Let Meta and Google show us which of our ads bring people to Voucha. Sets their cookies; never your posts or answers.'
  })]), el('span', {
    class: 'VouchaCookies-switch'
  }, [ads, el('span', {
    'aria-hidden': 'true'
  })])]), el('button', {
    type: 'button',
    class: 'Button Button--primary VouchaCookies-save',
    text: 'Save choices',
    onclick: () => choose(measure.checked, ads.checked)
  })]);
  if (!prefs) prefsPanel.hidden = true;
  const privacy = el('a', {
    href: '/p/1-privacy-policy',
    text: 'Privacy policy'
  });
  box = el('div', {
    class: 'VouchaCookies',
    role: 'dialog',
    'aria-label': 'Cookie choices',
    'aria-live': 'polite'
  }, [el('p', {
    class: 'VouchaCookies-text'
  }, [document.createTextNode('Voucha uses cookies to keep you signed in and, with your OK, to see which links, newsletters and ads bring people here, including cookies from Meta and Google. '), privacy]), el('div', {
    class: 'VouchaCookies-actions'
  }, [el('button', {
    type: 'button',
    class: 'Button VouchaCookies-choice',
    text: 'Accept',
    onclick: () => choose(true, true)
  }), el('button', {
    type: 'button',
    class: 'Button VouchaCookies-choice',
    text: 'Decline',
    onclick: () => choose(false, false)
  })]), el('button', {
    type: 'button',
    class: 'Button Button--link VouchaCookies-manage',
    text: 'Manage preferences',
    'aria-expanded': prefs ? 'true' : 'false',
    onclick: e => {
      prefsPanel.hidden = !prefsPanel.hidden;
      e.currentTarget.setAttribute('aria-expanded', prefsPanel.hidden ? 'false' : 'true');
    }
  }), prefsPanel]);
  if (current && currentAds) {
    // reopened from "Cookie settings": a way to close without changing anything
    box.appendChild(el('button', {
      type: 'button',
      class: 'VouchaCookies-close',
      'aria-label': 'Close',
      onclick: close
    }, [el('i', {
      class: 'fas fa-times',
      'aria-hidden': 'true'
    })]));
  }
  document.body.appendChild(box);
}
export default function vouchaCookieConsent() {
  window.vouchaCookieSettings = () => openCookieBanner(true);
  // Any link to #cookie-settings (sidebar footer, Privacy page) opens the choices
  document.addEventListener('click', e => {
    const a = e.target && e.target.closest && e.target.closest('a[href$="#cookie-settings"]');
    if (!a) return;
    e.preventDefault();
    openCookieBanner(true);
  });
  // Not chosen yet, or chose before the Advertising choice existed: ask (once)
  if (!read() || !readAds()) {
    // after the page has drawn, so it never delays the first paint
    setTimeout(() => openCookieBanner(false), 800);
  }
}